/** "Resend trip invite…" (plan §3, guest-visible): the invitation goes out again with the
 *  booking's existing invitation link — the guest receives it — and, unless suppressed,
 *  `tour_participant.created` is emitted too (it drives the event-based notifications
 *  and customer webhooks). The event policy is the operator's choice, default Emit (D5).
 *  The result names which invitation went out and links the event's deliveries. */
import { Box, Link, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { resendInvite, type BookingInviteResult } from '@/api/bookingActions';
import { GuestVisible } from '@/features/records/ledger/LedgerOnSave';
import { cond, deliveryLogsHref } from '@/utils/gridLinks';

import { ImmediateActionPopover } from './ImmediateActionPopover';
import { MONO_SX } from './panels/displays';

const SENT: Record<BookingInviteResult['notification'], string> = {
  guest: 'the guest’s trip invitation went out',
  staff: 'the guide invitation went out (a staff booking)',
  none: 'no invitation went out — the guest is excluded from chat',
};

export function InvitePopover({
  bookingId,
  guestName,
  anchorEl,
  onClose,
}: {
  bookingId: number;
  guestName: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  const [emit, setEmit] = useState(true);
  return (
    <ImmediateActionPopover
      anchorEl={anchorEl}
      onClose={onClose}
      title="Resend the trip invitation"
      confirmLabel="Send the invitation"
      run={() => resendInvite(bookingId, emit)}
      renderResult={(r) => (
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Typography variant="body2" component="p" sx={{ m: 0 }}>
            Sent: {SENT[r.notification]}.
          </Typography>
          {r.event ? (
            <Typography variant="body2" component="p" sx={{ m: 0 }}>
              <Box component="span" sx={MONO_SX}>
                {r.event.code}
              </Box>{' '}
              emitted ·{' '}
              <Link component={RouterLink} to={deliveryLogsHref([cond('tag', r.event.tag)])} target="_blank" rel="noopener">
                Deliveries
              </Link>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
              No event (suppressed).
            </Typography>
          )}
        </Box>
      )}
      dataKey="invite"
    >
      <Typography variant="body2" component="p" sx={{ m: 0 }}>
        Sends {guestName} the trip invitation again, with the booking’s existing invitation link.
        <GuestVisible />
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" component="span" id={`invite-event-${bookingId}`}>
          <Box component="span" sx={MONO_SX}>
            tour_participant.created
          </Box>
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={emit ? 'emit' : 'suppress'}
          onChange={(_, v: 'emit' | 'suppress' | null) => v && setEmit(v === 'emit')}
          aria-labelledby={`invite-event-${bookingId}`}
        >
          <ToggleButton value="emit" sx={{ px: 1.25, py: 0.25, textTransform: 'none' }}>
            Emit
          </ToggleButton>
          <ToggleButton value="suppress" sx={{ px: 1.25, py: 0.25, textTransform: 'none' }}>
            Suppress
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
        {emit
          ? 'The event drives the event-based notifications and customer webhooks too.'
          : 'Only the invitation goes out; no event, no webhooks.'}
      </Typography>
    </ImmediateActionPopover>
  );
}
